// src/components/Admin/tabs/OverviewTab.jsx
import React from "react";
import { Plus, Trash2 } from "lucide-react";
import StatusBadge from "../shared/StatusBadge";

const OverviewTab = ({
  clientInfo,
  clientSlug,
  states,
  status,
  features,
  globalReports = [],
  isNew,
  onClientChange,
  onSlugChange,
  onStatesChange,
  onStatusChange,
  onFeaturesChange,
  onGlobalReportsChange,
}) => {
  const updateClient = (key, value) => {
    onClientChange({ ...clientInfo, [key]: value });
  };

  // ---- State helpers ----
  const addState = () => {
    onStatesChange([
      ...states,
      {
        abbreviation: "",
        name: "",
        fipsCode: "",
        mapCenter: [39.8283, -98.5795], // center of US
        defaultZoom: 7,
        minZoom: 5,
        maxZoom: 18,
      },
    ]);
  };

  const updateState = (idx, key, value) => {
    onStatesChange(
      states.map((s, i) => (i === idx ? { ...s, [key]: value } : s)),
    );
  };

  const removeState = (idx) => {
    onStatesChange(states.filter((_, i) => i !== idx));
  };

  // ---- Feature helpers ----
  const updateFeature = (featureKey, subKey, value) => {
    onFeaturesChange({
      ...features,
      [featureKey]: { ...features[featureKey], [subKey]: value },
    });
  };

  return (
    <div className="space-y-6">
      {/* ============ CLIENT INFO ============ */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">
          Client Information
        </h3>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Client name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Client Name <span className="text-red-400">*</span>
            </label>
            <input
              type="text"
              value={clientInfo.name || ""}
              onChange={(e) => updateClient("name", e.target.value)}
              placeholder="Laundry Lifesaver"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              URL Slug <span className="text-red-400">*</span>
              {!isNew && (
                <span className="text-gray-400 font-normal ml-1">
                  (read-only)
                </span>
              )}
            </label>
            <div className="flex items-center">
              <span className="text-xs text-gray-400 mr-1 whitespace-nowrap">
                heatmaphq.com/
              </span>
              <input
                type="text"
                value={clientSlug || ""}
                onChange={(e) =>
                  onSlugChange(
                    e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ""),
                  )
                }
                disabled={!isNew}
                placeholder="laundry-lifesaver"
                className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary disabled:bg-gray-100 disabled:text-gray-500"
              />
            </div>
          </div>

          {/* Project name */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Project Name
            </label>
            <input
              type="text"
              value={clientInfo.projectName || ""}
              onChange={(e) => updateClient("projectName", e.target.value)}
              placeholder="Connecticut Market Analysis"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
            />
          </div>

          {/* Contact email */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Contact Email
            </label>
            <input
              type="email"
              value={clientInfo.contactEmail || ""}
              onChange={(e) => updateClient("contactEmail", e.target.value)}
              placeholder="owner@business.com"
              className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
            />
          </div>

          {/* Primary color */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Primary Color
            </label>
            <div className="flex items-center gap-2">
              <div className="relative">
                <div
                  className="w-9 h-9 rounded-lg border border-gray-300 cursor-pointer"
                  style={{
                    backgroundColor: clientInfo.primaryColor || "#030712",
                  }}
                />
                <input
                  type="color"
                  value={clientInfo.primaryColor || "#030712"}
                  onChange={(e) => updateClient("primaryColor", e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              <input
                type="text"
                value={clientInfo.primaryColor || ""}
                onChange={(e) => updateClient("primaryColor", e.target.value)}
                placeholder="#030712"
                className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
              />
            </div>
          </div>

          {/* Accent color */}
          <div>
            <label className="block text-xs font-medium text-gray-600 mb-1">
              Accent Color
            </label>
            <div className="flex items-center gap-2">
              <div className="relative">
                <div
                  className="w-9 h-9 rounded-lg border border-gray-300 cursor-pointer"
                  style={{
                    backgroundColor: clientInfo.accentColor || "#15803d",
                  }}
                />
                <input
                  type="color"
                  value={clientInfo.accentColor || "#15803d"}
                  onChange={(e) => updateClient("accentColor", e.target.value)}
                  className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
                />
              </div>
              <input
                type="text"
                value={clientInfo.accentColor || ""}
                onChange={(e) => updateClient("accentColor", e.target.value)}
                placeholder="#15803d"
                className="flex-1 px-3 py-2 text-sm font-mono border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary/30 focus:border-secondary"
              />
            </div>
          </div>
        </div>
      </div>

      {/* ============ STATUS ============ */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Status</h3>
        <div className="flex items-center gap-4">
          {["draft", "active", "inactive"].map((s) => (
            <label
              key={s}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition ${
                status === s
                  ? "border-secondary bg-secondary/5"
                  : "border-gray-200 hover:border-gray-300"
              }`}
            >
              <input
                type="radio"
                name="status"
                value={s}
                checked={status === s}
                onChange={() => onStatusChange(s)}
                className="text-secondary focus:ring-secondary"
              />
              <StatusBadge status={s} />
            </label>
          ))}
        </div>
        <p className="text-xs text-gray-400 mt-2">
          Only active clients are visible at their public URL. Draft clients can
          be configured but {"won't"} load for end users.
        </p>
      </div>

      {/* ============ STATES ============ */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              States & Map Settings
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Configure map center and zoom for each state the client operates
              in
            </p>
          </div>
          <button
            type="button"
            onClick={addState}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-secondary hover:bg-secondary/10 border border-secondary/30 rounded-lg transition"
          >
            <Plus size={14} />
            Add State
          </button>
        </div>

        {states.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">
            No states configured. Add a state to set up map boundaries.
          </p>
        )}

        <div className="space-y-3">
          {states.map((state, idx) => (
            <div
              key={idx}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="grid grid-cols-3 gap-2 flex-1">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Abbreviation *
                    </label>
                    <input
                      type="text"
                      value={state.abbreviation || ""}
                      onChange={(e) =>
                        updateState(
                          idx,
                          "abbreviation",
                          e.target.value.toUpperCase().slice(0, 2),
                        )
                      }
                      placeholder="CT"
                      maxLength={2}
                      className="w-full px-2.5 py-1.5 text-sm font-mono uppercase border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Full Name *
                    </label>
                    <input
                      type="text"
                      value={state.name || ""}
                      onChange={(e) => updateState(idx, "name", e.target.value)}
                      placeholder="Connecticut"
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      FIPS Code
                    </label>
                    <input
                      type="text"
                      value={state.fipsCode || ""}
                      onChange={(e) =>
                        updateState(idx, "fipsCode", e.target.value)
                      }
                      placeholder="09"
                      className="w-full px-2.5 py-1.5 text-sm font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeState(idx)}
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition mt-5"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              {/* Map settings */}
              <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Center Lat
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={state.mapCenter?.[0] ?? ""}
                    onChange={(e) =>
                      updateState(idx, "mapCenter", [
                        Number(e.target.value),
                        state.mapCenter?.[1] ?? 0,
                      ])
                    }
                    placeholder="41.6032"
                    className="w-full px-2 py-1.5 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Center Lng
                  </label>
                  <input
                    type="number"
                    step="0.0001"
                    value={state.mapCenter?.[1] ?? ""}
                    onChange={(e) =>
                      updateState(idx, "mapCenter", [
                        state.mapCenter?.[0] ?? 0,
                        Number(e.target.value),
                      ])
                    }
                    placeholder="-73.0877"
                    className="w-full px-2 py-1.5 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Default Zoom
                  </label>
                  <input
                    type="number"
                    value={state.defaultZoom ?? ""}
                    onChange={(e) =>
                      updateState(idx, "defaultZoom", Number(e.target.value))
                    }
                    min={1}
                    max={20}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Min Zoom
                  </label>
                  <input
                    type="number"
                    value={state.minZoom ?? ""}
                    onChange={(e) =>
                      updateState(idx, "minZoom", Number(e.target.value))
                    }
                    min={1}
                    max={20}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
                  />
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">
                    Max Zoom
                  </label>
                  <input
                    type="number"
                    value={state.maxZoom ?? ""}
                    onChange={(e) =>
                      updateState(idx, "maxZoom", Number(e.target.value))
                    }
                    min={1}
                    max={22}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30"
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ============ FEATURES ============ */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <h3 className="text-sm font-semibold text-gray-800 mb-4">Features</h3>
        <div className="space-y-3">
          {[
            {
              key: "search",
              label: "Search",
              desc: "Allow users to search geographies",
            },
            { key: "download", label: "Download", desc: "Allow data exports" },
            { key: "reports", label: "Reports", desc: "Show the reports page" },
            {
              key: "competitors",
              label: "Competitors",
              desc: "Show competitor locations on map",
              hasLabel: true,
            },
            {
              key: "opportunities",
              label: "Opportunities",
              desc: "Show opportunity locations on map",
              hasLabel: true,
            },
            {
              key: "proxyBusinesses",
              label: "Proxy Businesses",
              desc: "Show proxy business locations",
            },
          ].map(({ key, label, desc, hasLabel }) => (
            <div key={key}>
              <label className="flex items-center justify-between py-2 px-3 rounded-lg hover:bg-gray-50 cursor-pointer">
                <div>
                  <span className="text-sm font-medium text-gray-700">
                    {label}
                  </span>
                  <p className="text-xs text-gray-400">{desc}</p>
                </div>
                <input
                  type="checkbox"
                  checked={features?.[key]?.enabled || false}
                  onChange={(e) =>
                    updateFeature(key, "enabled", e.target.checked)
                  }
                  className="rounded border-gray-300 text-secondary focus:ring-secondary"
                />
              </label>
              {hasLabel && features?.[key]?.enabled && (
                <div className="ml-3 pl-3 mt-1 mb-1">
                  <label className="block text-xs text-gray-500 mb-1">
                    Toggle label (shown on map)
                  </label>
                  <input
                    type="text"
                    value={features?.[key]?.label || ""}
                    onChange={(e) =>
                      updateFeature(key, "label", e.target.value)
                    }
                    placeholder={label}
                    className="w-48 px-2.5 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ============ GLOBAL REPORTS ============ */}
      <div className="bg-white rounded-xl border border-gray-200 p-5">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="text-sm font-semibold text-gray-800">
              Global Reports
            </h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Client-wide reports not tied to any avatar or map (FAQ,
              opportunities list, etc.)
            </p>
          </div>
          <button
            type="button"
            onClick={() =>
              onGlobalReportsChange([
                ...globalReports,
                {
                  id: "",
                  title: "",
                  fileName: "",
                  storagePath: "",
                  type: "file",
                  icon: "file-text",
                },
              ])
            }
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-secondary hover:bg-secondary/10 border border-secondary/30 rounded-lg transition"
          >
            <Plus size={14} />
            Add Report
          </button>
        </div>

        {globalReports.length === 0 && (
          <p className="text-sm text-gray-400 text-center py-6">
            No global reports configured. Add a report to define FAQ,
            opportunity lists, or other client-wide files.
          </p>
        )}

        <div className="space-y-3">
          {globalReports.map((report, idx) => (
            <div
              key={idx}
              className="border border-gray-200 rounded-lg p-4 bg-gray-50"
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 flex-1">
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Report ID <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={report.id || ""}
                      onChange={(e) => {
                        const updated = globalReports.map((r, i) =>
                          i === idx ? { ...r, id: e.target.value } : r,
                        );
                        onGlobalReportsChange(updated);
                      }}
                      placeholder="faq"
                      className="w-full px-2.5 py-1.5 text-sm font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Title <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="text"
                      value={report.title || ""}
                      onChange={(e) => {
                        const updated = globalReports.map((r, i) =>
                          i === idx ? { ...r, title: e.target.value } : r,
                        );
                        onGlobalReportsChange(updated);
                      }}
                      placeholder="Frequently Asked Questions"
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-1">
                      Icon
                    </label>
                    <select
                      value={report.icon || "file-text"}
                      onChange={(e) => {
                        const updated = globalReports.map((r, i) =>
                          i === idx ? { ...r, icon: e.target.value } : r,
                        );
                        onGlobalReportsChange(updated);
                      }}
                      className="w-full px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                    >
                      <option value="file-text">file-text</option>
                      <option value="table">table</option>
                      <option value="help-circle">help-circle</option>
                      <option value="book-open">book-open</option>
                      <option value="briefcase">briefcase</option>
                      <option value="map-pin">map-pin</option>
                      <option value="database">database</option>
                    </select>
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() =>
                    onGlobalReportsChange(
                      globalReports.filter((_, i) => i !== idx),
                    )
                  }
                  className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition mt-5"
                >
                  <Trash2 size={16} />
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    File Name <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={report.fileName || ""}
                    onChange={(e) => {
                      const updated = globalReports.map((r, i) =>
                        i === idx ? { ...r, fileName: e.target.value } : r,
                      );
                      onGlobalReportsChange(updated);
                    }}
                    placeholder="faq.md"
                    className="w-full px-2.5 py-1.5 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1">
                    Storage Path <span className="text-red-400">*</span>
                  </label>
                  <input
                    type="text"
                    value={report.storagePath || ""}
                    onChange={(e) => {
                      const updated = globalReports.map((r, i) =>
                        i === idx ? { ...r, storagePath: e.target.value } : r,
                      );
                      onGlobalReportsChange(updated);
                    }}
                    placeholder="reports/faq.md"
                    className="w-full px-2.5 py-1.5 text-xs font-mono border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                  />
                </div>
              </div>

              <div className="mt-2">
                <label className="block text-xs font-medium text-gray-500 mb-1">
                  Type
                </label>
                <select
                  value={report.type || "file"}
                  onChange={(e) => {
                    const updated = globalReports.map((r, i) =>
                      i === idx ? { ...r, type: e.target.value } : r,
                    );
                    onGlobalReportsChange(updated);
                  }}
                  className="w-48 px-2.5 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-secondary/30 focus:border-secondary"
                >
                  <option value="markdown">Markdown</option>
                  <option value="file">File (PDF/XLSX)</option>
                </select>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default OverviewTab;
